import { byId } from '../dom.ts';
import { Layout, notify, state, subscribe } from '../state.ts';
import {
  LAYOUT_MIN,
  addFiles,
  intakeError,
  moveClip,
  removeClip,
  reorderClip,
  setOffset,
  setOpacity,
  swapFirstTwo,
} from '../clips.ts';
import { cancelExport, isVideoExportSupported, runExport } from '../export.ts';

const LAYOUTS: Array<{ id: Layout; label: string }> = [
  { id: 'single', label: 'Single' },
  { id: 'wipe', label: 'Wipe A/B' },
  { id: 'side-by-side', label: 'Side by side' },
  { id: 'top-bottom', label: 'Top / bottom' },
  { id: 'pip', label: 'PIP' },
  { id: 'grid', label: 'Grid' },
  { id: 'dominant-right', label: 'Big + right' },
  { id: 'dominant-bottom', label: 'Big + bottom' },
];

export function initSidebar(): void {
  const fileInput = byId<HTMLInputElement>('file-input');
  const clipList = byId<HTMLDivElement>('clip-list');
  const layoutPicker = byId<HTMLDivElement>('layout-picker');
  const exportBtn = byId<HTMLButtonElement>('export-btn');
  const cancelBtn = byId<HTMLButtonElement>('export-cancel');
  const progressWrap = byId<HTMLDivElement>('export-progress');
  const progressBar = byId<HTMLDivElement>('export-bar');
  const statusEl = byId<HTMLParagraphElement>('export-status');
  const resSel = byId<HTMLSelectElement>('export-res');
  const fpsSel = byId<HTMLSelectElement>('export-fps');

  byId('add-clip').addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    if (fileInput.files?.length) void addFiles(fileInput.files);
    fileInput.value = '';
  });

  // ----- layout picker -----
  for (const { id, label } of LAYOUTS) {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.dataset.layout = id;
    btn.addEventListener('click', () => {
      state.layout = id;
      notify();
    });
    layoutPicker.appendChild(btn);
  }

  byId('swap-btn').addEventListener('click', swapFirstTwo);

  const pipCorner = byId<HTMLSelectElement>('pip-corner');
  pipCorner.addEventListener('change', () => {
    state.pipCorner = pipCorner.value as typeof state.pipCorner;
    notify();
  });

  // ----- export -----
  resSel.addEventListener('change', () => {
    state.exportRes = resSel.value as typeof state.exportRes;
    notify();
  });
  fpsSel.addEventListener('change', () => {
    state.exportFps = fpsSel.value as typeof state.exportFps;
    notify();
  });

  let lastMessage = '';
  exportBtn.addEventListener('click', () => {
    lastMessage = '';
    void runExport((done, total) => {
      progressBar.style.width = `${(done / total) * 100}%`;
      statusEl.textContent = `Encoding frame ${done} / ${total}…`;
    }).then((result) => {
      lastMessage = result.message;
      notify();
    });
  });
  cancelBtn.addEventListener('click', cancelExport);

  // ----- clip list (rebuilt only when the set/order changes; values synced in place) -----
  let listSignature = '';

  clipList.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!btn) return;
    const id = btn.closest<HTMLElement>('[data-id]')?.dataset.id;
    if (!id) return;
    if (btn.dataset.act === 'remove') removeClip(id);
    if (btn.dataset.act === 'up') moveClip(id, -1);
    if (btn.dataset.act === 'down') moveClip(id, 1);
  });

  clipList.addEventListener('input', (e) => {
    const input = e.target as HTMLInputElement;
    const id = input.closest<HTMLElement>('[data-id]')?.dataset.id;
    if (!id) return;
    if (input.dataset.act === 'opacity') setOpacity(id, Number(input.value));
    if (input.dataset.act === 'offset' && e.type === 'input' && input.value !== '' && input.value !== '-') {
      setOffset(id, Number(input.value));
    }
  });

  // Drag re-ordering. Items are only armed for dragging when the gesture
  // starts on the card header — otherwise HTML5 drag swallows the pointer
  // drags meant for the opacity/offset inputs.
  let dragId: string | null = null;
  clipList.addEventListener('pointerdown', (e) => {
    const target = e.target as HTMLElement;
    const item = target.closest<HTMLElement>('.clip-item');
    if (item) item.draggable = !!target.closest('.clip-head');
  });
  clipList.addEventListener('dragstart', (e) => {
    const item = (e.target as HTMLElement).closest<HTMLElement>('.clip-item');
    if (!item) return;
    dragId = item.dataset.id ?? null;
    item.classList.add('dragging');
    e.dataTransfer?.setData('text/plain', dragId ?? '');
  });
  clipList.addEventListener('dragend', (e) => {
    (e.target as HTMLElement).closest('.clip-item')?.classList.remove('dragging');
    dragId = null;
  });
  clipList.addEventListener('dragover', (e) => {
    if (!dragId) return;
    e.preventDefault();
    e.stopPropagation();
    const over = (e.target as HTMLElement).closest<HTMLElement>('.clip-item');
    for (const el of clipList.querySelectorAll('.clip-item')) el.classList.remove('drag-over');
    if (over && over.dataset.id !== dragId) over.classList.add('drag-over');
  });
  clipList.addEventListener('drop', (e) => {
    if (!dragId) return;
    e.preventDefault();
    e.stopPropagation();
    const over = (e.target as HTMLElement).closest<HTMLElement>('.clip-item');
    if (over && over.dataset.id !== dragId) reorderClip(dragId, over.dataset.id ?? null);
    for (const el of clipList.querySelectorAll('.clip-item')) el.classList.remove('drag-over');
    dragId = null;
  });

  function renderClipList(): void {
    const signature = state.clips
      .map((c) => `${c.id}:${c.name}:${c.thumb ? 1 : 0}:${c.error ?? ''}:${c.scrubCache}`)
      .join('|');
    if (signature !== listSignature) {
      listSignature = signature;
      clipList.innerHTML = '';
      state.clips.forEach((clip, i) => {
        const item = document.createElement('div');
        item.className = 'clip-item';
        item.dataset.id = clip.id;

        const cacheTag =
          clip.scrubCache === 'ready'
            ? ' · ⚡scrub'
            : clip.scrubCache === 'building'
              ? ' · caching…'
              : '';
        const meta =
          clip.kind === 'video'
            ? `${clip.width}×${clip.height} · ${clip.fps}fps · ${clip.durationSec.toFixed(2)}s${cacheTag}`
            : `${clip.width}×${clip.height} · image`;

        item.innerHTML = `
          <div class="clip-head">
            ${clip.thumb ? `<img class="clip-thumb" src="${clip.thumb}" alt="" />` : '<div class="clip-thumb"></div>'}
            <div class="clip-title">
              <div class="clip-name"><span class="clip-badge">${i + 1}</span>${escapeHtml(clip.name)}</div>
              <div class="clip-meta">${meta}</div>
            </div>
            <div class="clip-btns">
              <button data-act="up" title="Move up">↑</button>
              <button data-act="down" title="Move down">↓</button>
              <button data-act="remove" title="Remove">✕</button>
            </div>
          </div>
          <div class="clip-controls">
            <span title="Timeline frame where this clip starts">Offset</span>
            <input type="number" data-act="offset" value="${clip.offsetFrames}" step="1" />
            <span>Opacity</span>
            <input type="range" data-act="opacity" min="0" max="1" step="0.05" value="${clip.opacity}" />
          </div>
          ${clip.error ? `<div class="clip-error">${escapeHtml(clip.error)}</div>` : ''}
        `;
        clipList.appendChild(item);
      });
    }

    // In-place value/badge sync (don't clobber focused inputs).
    state.clips.forEach((clip, i) => {
      const item = clipList.querySelector<HTMLElement>(`[data-id="${clip.id}"]`);
      if (!item) return;
      const badge = item.querySelector('.clip-badge');
      if (badge && badge.textContent !== String(i + 1)) badge.textContent = String(i + 1);
      const offset = item.querySelector<HTMLInputElement>('input[data-act="offset"]');
      if (offset && document.activeElement !== offset) offset.value = String(clip.offsetFrames);
      const opacity = item.querySelector<HTMLInputElement>('input[data-act="opacity"]');
      if (opacity && document.activeElement !== opacity) opacity.value = String(clip.opacity);
      const upBtn = item.querySelector<HTMLButtonElement>('button[data-act="up"]');
      const downBtn = item.querySelector<HTMLButtonElement>('button[data-act="down"]');
      if (upBtn) upBtn.disabled = i === 0;
      if (downBtn) downBtn.disabled = i === state.clips.length - 1;
    });

    let errEl = clipList.querySelector<HTMLElement>('.clip-intake-error');
    if (intakeError) {
      if (!errEl) {
        errEl = document.createElement('div');
        errEl.className = 'clip-error clip-intake-error';
        clipList.appendChild(errEl);
      }
      errEl.textContent = intakeError;
    } else {
      errEl?.remove();
    }
  }

  subscribe(() => {
    const n = state.clips.length;
    byId('layout-section').hidden = n < 2;
    byId('export-section').hidden = n === 0;
    byId('swap-row').hidden = n < 2;
    byId('pip-corner-row').hidden = state.layout !== 'pip';
    byId('wipe-hint').hidden = state.layout !== 'wipe';

    for (const btn of layoutPicker.querySelectorAll<HTMLButtonElement>('button')) {
      const id = btn.dataset.layout as Layout;
      btn.hidden = n < LAYOUT_MIN[id];
      btn.classList.toggle('active', state.layout === id);
    }

    if (document.activeElement !== resSel) resSel.value = state.exportRes;
    if (document.activeElement !== fpsSel) fpsSel.value = state.exportFps;

    exportBtn.disabled = state.exporting || n === 0 || !isVideoExportSupported();
    exportBtn.hidden = state.exporting;
    cancelBtn.hidden = !state.exporting;
    progressWrap.hidden = !state.exporting;
    if (!state.exporting) {
      progressBar.style.width = '0';
      statusEl.textContent = !isVideoExportSupported()
        ? 'Export needs a Chromium browser with WebCodecs (https or localhost).'
        : lastMessage;
    }

    renderClipList();
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
